import { addMinutes } from "date-fns";
import { groupBy } from "lodash-es";
import { useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import type { SurveyDetailsV2Dto, UpdateSurveyRequestDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import {
  DynamicRepeater,
  type DynamicRepeaterDragEndEvent,
  type DynamicRepeaterDragStartEvent,
} from "@/components/DynamicRepeater/DynamicRepeater";
import { DynamicRepeaterItem } from "@/components/DynamicRepeater/DynamicRepeaterItem";
import { DynamicRepeaterItemOverlay } from "@/components/DynamicRepeater/DynamicRepeaterItemOverlay";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import type { Question } from "@/components/Survey/FormQuestion";
import { FormQuestion } from "@/components/Survey/FormQuestion";
import { Wizard, WizardStep } from "@/components/Wizard/Wizard";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { getEndDateAfterStartChange } from "@/helpers/date-range";
import { getFlagIcon } from "@/helpers/flags";
import { createRequiredStringRule } from "@/helpers/rules";
import { calculateSurveyEndDate } from "@/helpers/survey-dates";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { feelingAtHomeTemplate, questionTypes, surveyCategories } from "@/queries/surveys/constants";
import { routes } from "@/routes";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";

export interface SurveyFormProps {
  isEditMode: boolean;
  contentLanguageId: Languages | undefined;
  defaultFormValues: CreateOrEditSurveyFormValues;
  groups: { id: string | undefined; name: string }[];
  onSubmit: (payload: UpdateSurveyRequestDto, category: SurveyDetailsV2Dto["category"]) => Promise<unknown>;
  isSubmitting: boolean;
  isPublished: boolean;
}

export interface CreateOrEditSurveyFormValues {
  type: SurveyDetailsV2Dto["type"];
  category: SurveyDetailsV2Dto["category"];
  startDate: Date | undefined;
  endDate: Date | undefined;
  group?: { id: string | undefined; name: string };
  title: string;
  description: string;
  thankYouMessage: string;
  questions: Question[];
  internal: boolean;
}

const TITLE_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 500;

export function SurveyForm({
  isEditMode,
  contentLanguageId,
  defaultFormValues,
  groups,
  onSubmit,
  isSubmitting,
  isPublished,
}: SurveyFormProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const [dragItemId, setDragItemId] = useState<number | null>(null);
  const form = useForm<CreateOrEditSurveyFormValues>({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });
  const { fields, update, append, remove, move } = useFieldArray<CreateOrEditSurveyFormValues, "questions">({
    control: form.control,
    name: "questions",
  });

  const start = useWatch({ control: form.control, name: "startDate" });
  const category = useWatch({ control: form.control, name: "category" });
  const canLeaveFirstStep = async () =>
    await form.trigger(["type", "startDate", "endDate", "group", "title", "description", "thankYouMessage"]);
  const canLeaveFinal = async () => await form.trigger();

  const [minStart] = useState(() => addMinutes(new Date(), 15));

  const project = useProject();

  const handleStartDateChange = (newStartDate: Date | undefined) => {
    const end = getEndDateAfterStartChange(start, form.getValues("endDate"), newStartDate, calculateSurveyEndDate);
    form.setValue("endDate", end, { shouldDirty: true, shouldValidate: true });
  };

  async function handleSave() {
    const formValues = form.getValues();

    if (!formValues.startDate) {
      throw new Error("Missing start date");
    }

    if (!formValues.endDate) {
      throw new Error("Missing end date");
    }

    if (!formValues.questions.length) {
      showFlashToast({
        variant: "alert",
        message: t("page.surveys-create-or-edit.error.questions.min"),
      });

      return;
    }

    let doesNotHaveMultipleOptions = false;
    let hasRepeatedOptions = false;
    formValues.questions.forEach((question) => {
      if (question.type !== "multipleChoice") {
        return;
      }

      if (!question.options.length || question.options.length < 2) {
        doesNotHaveMultipleOptions = true;
      }

      const groupedOptions = groupBy(question.options, (val) => val.option);
      for (const group in groupedOptions) {
        if (groupedOptions[group].length > 1) {
          hasRepeatedOptions = true;
        }
      }
    });

    if (doesNotHaveMultipleOptions) {
      showFlashToast({
        variant: "alert",
        message: t("page.surveys-create-or-edit.error.multiple-choice.less-than-required"),
      });

      return;
    }

    if (hasRepeatedOptions) {
      showFlashToast({
        variant: "alert",
        message: t("page.surveys-create-or-edit.error.multiple-choice.unique"),
      });

      return;
    }

    const payload = {
      title: formValues.title,
      description: formValues.description,
      startDate: formValues.startDate.toISOString(),
      endDate: formValues.endDate.toISOString(),
      surveyCompleted: formValues.thankYouMessage,
      questions: formValues.questions.map((question, idx) => ({
        id: question.qId,
        sorting: idx,
        type: question.type,
        question: question.question,
        offeredAnswers: question.options.length
          ? question.options.map((option) => ({ id: option.oId, answer: option.option }))
          : undefined,
        useForAnalysis: !!question.useForAnalysis,
      })),
      groupId: formValues.group?.id,
      internal: formValues.internal,
    } satisfies UpdateSurveyRequestDto;

    await onSubmit(payload, formValues.category);
  }

  function handleDragStart(event: DynamicRepeaterDragStartEvent) {
    setDragItemId(event.active.data.current?.sortable.index);
  }

  function handleDragEnd(event: DynamicRepeaterDragEndEvent) {
    setDragItemId(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      move(active.data.current?.sortable.index, over?.data.current?.sortable.index);
    }
  }

  function handleCategoryChange(newCategory: SurveyDetailsV2Dto["category"]) {
    if (newCategory === "other") {
      form.reset();

      return;
    }

    remove(0);

    form.setValue("title", t(feelingAtHomeTemplate.title, { projectName: project.name }));
    form.setValue("description", t(feelingAtHomeTemplate.description, { projectName: project.name }));
    form.setValue("thankYouMessage", t(feelingAtHomeTemplate.thankYouMessage, { projectName: project.name }));

    for (const element of feelingAtHomeTemplate.questions) {
      append({
        type: element.type,
        question: t(element.title, { projectName: project.name }),
        options:
          "options" in element
            ? element.options?.map((option) => ({
                option: t(option.option, { projectName: project.name }),
              }))
            : [],
        useForAnalysis: "useForAnalysis" in element && element.useForAnalysis,
      });
    }
  }

  const isUseForAnalysisSelected = useMemo(() => {
    return fields.some((field) => field.useForAnalysis);
  }, [fields]);

  const title = isEditMode ? t("page.surveys.create-or-edit.title.edit") : t("page.surveys.create-or-edit.title.new");
  const flagIcon = getFlagIcon({
    languageId: contentLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.surveys.list.title"),
              to: routes.surveys.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={form} onSubmit={handleSave}>
        <Form.Container>
          <Wizard
            actionsText={{
              finish: isEditMode
                ? t("component.wizard.action.edit.finish")
                : t("component.wizard.action.create.finish"),
            }}
            onFinish={form.handleSubmit(handleSave)}
            strictOrder={false}
            isSubmitting={isSubmitting}
          >
            <WizardStep
              id={1}
              canLeave={canLeaveFirstStep}
              hasFinish={isEditMode}
              title={t("component.wizard.step-general")}
            >
              <Stack gap="lg">
                <Notice
                  icon={<Translate01Icon />}
                  variant="special"
                  message={
                    isEditMode
                      ? t("common.label.you-are-editing-in-original-content-language")
                      : t("common.label.your-input-will-be-auto-translated")
                  }
                />
                <Form.Body>
                  <FormField label={t("page.surveys.create-or-edit.form.title.label")} actions={flagIcon} required>
                    <FormInput<CreateOrEditSurveyFormValues>
                      name="title"
                      placeholder={t("page.surveys.create-or-edit.form.title.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.title.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: TITLE_MAX_LENGTH,
                          }),
                          value: TITLE_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  <FormField
                    label={t("page.surveys.create-or-edit.form.description.label")}
                    actions={flagIcon}
                    required
                  >
                    <FormTextArea<CreateOrEditSurveyFormValues>
                      name="description"
                      placeholder={t("page.surveys.create-or-edit.form.description.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.description.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: DESCRIPTION_MAX_LENGTH,
                          }),
                          value: DESCRIPTION_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  <FormField
                    label={t("page.surveys.create-or-edit.form.thanks-message.label")}
                    actions={flagIcon}
                    required
                  >
                    <FormTextArea<CreateOrEditSurveyFormValues>
                      name="thankYouMessage"
                      placeholder={t("page.surveys.create-or-edit.form.thanks-message.placeholder")}
                      rows={1}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(
                            t,
                            "page.surveys.create-or-edit.form.thanks-message.label",
                          ),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: DESCRIPTION_MAX_LENGTH,
                          }),
                          value: DESCRIPTION_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  {sessionUser.isSuperAdmin && (
                    <FormField label={t("page.surveys.create-or-edit.form.category.label")} required>
                      <FormSelect<CreateOrEditSurveyFormValues, SurveyDetailsV2Dto["category"]>
                        name="category"
                        placeholder={t("page.surveys.create-or-edit.form.category.placeholder")}
                        items={surveyCategories}
                        onChange={(x) => handleCategoryChange(x!)}
                        keySelector={(x) => x}
                        renderOption={(x) => {
                          switch (x) {
                            case "other":
                              return t("page.surveys.categories.other");
                            case "feelAtHome":
                              return t("page.surveys.categories.feel-at-home");
                          }
                        }}
                        rules={{
                          required: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.type.label"),
                          }),
                        }}
                        disabled={isEditMode}
                      />
                    </FormField>
                  )}
                  <FormField label={t("page.surveys.create-or-edit.form.starts-at.label")} required>
                    <FormDatePicker<CreateOrEditSurveyFormValues>
                      data-testid="survey-start-date-input"
                      name="startDate"
                      id="startDate"
                      type="datetime"
                      min={minStart}
                      onChange={handleStartDateChange}
                      rules={{
                        validate: {
                          laterThanMin: (startDate) => {
                            if (
                              startDate == null ||
                              (isEditMode && !!defaultFormValues.startDate && defaultFormValues.startDate < new Date())
                            ) {
                              return undefined;
                            }

                            return startDate < minStart
                              ? t("page.surveys.create-or-edit.form.starts-at.error.must-be-in-future")
                              : undefined;
                          },
                        },
                        required: {
                          message: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.starts-at.label"),
                          }),
                          value: true,
                        },
                      }}
                      disabled={!!defaultFormValues.startDate && defaultFormValues.startDate < clockDate}
                    />
                  </FormField>
                  <FormField label={t("page.surveys.create-or-edit.form.ends-at.label")} required>
                    <FormDatePicker<CreateOrEditSurveyFormValues>
                      data-testid="survey-end-date-input"
                      name="endDate"
                      id="endDate"
                      type="datetime"
                      min={start ? addMinutes(start, 1) : minStart}
                      rules={{
                        validate: {
                          laterThanStart: (endDate, formValues) => {
                            if (endDate == null) {
                              return undefined;
                            }

                            return formValues.startDate && endDate <= formValues.startDate
                              ? t("page.surveys.create-or-edit.form.ends-at.error.later-than-start-date")
                              : undefined;
                          },
                        },
                        required: {
                          message: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.ends-at.label"),
                          }),
                          value: true,
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.surveys.create-or-edit.form.group.label")}>
                    <FormSelect<CreateOrEditSurveyFormValues, { id: string | undefined; name: string }>
                      name="group"
                      placeholder={t("page.surveys.create-or-edit.form.group.placeholder")}
                      items={groups}
                      keySelector={(x) => x.id || "undefined"}
                      renderOption={(x) => x.name}
                      disabled={isPublished}
                    />
                  </FormField>
                  {sessionUser.isSuperAdmin && (
                    <FormField label="">
                      <FormCheckbox<CreateOrEditSurveyFormValues>
                        name="internal"
                        label={t("page.surveys.create-or-edit.internal.label")}
                      />
                    </FormField>
                  )}
                </Form.Body>
              </Stack>
            </WizardStep>
            <WizardStep
              id={2}
              canLeave={canLeaveFinal}
              hasFinish={isEditMode}
              title={t("page.surveys.create-or-edit.wizard.step-questions")}
            >
              <Form.Body>
                <DynamicRepeater
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  itemIds={fields.map((field) => field.id)}
                >
                  {fields.map((question, idx) => {
                    const canDelete =
                      fields.length > 1 &&
                      (defaultFormValues.startDate ? defaultFormValues.startDate > clockDate : true);

                    return (
                      <DynamicRepeaterItem
                        key={question.id}
                        id={question.id}
                        title={t("page.surveys.create-or-edit.question.label", { count: idx + 1 })}
                        headerActions={
                          canDelete ? (
                            <IconButton
                              size="sm"
                              variant="filled"
                              tone="error"
                              title={t("page.surveys.create-or-edit.question.delete")}
                              onClick={() => remove(idx)}
                              icon={<Trash02Icon />}
                            />
                          ) : undefined
                        }
                      >
                        <FormQuestion
                          idx={idx}
                          control={form.control}
                          updateField={update}
                          types={questionTypes}
                          question={question}
                          showOptions={question.type === "multipleChoice"}
                          showUseForAnalysisOption={category === "feelAtHome"}
                          isUseForAnalysisDisabled={isUseForAnalysisSelected && !question.useForAnalysis}
                        />
                      </DynamicRepeaterItem>
                    );
                  })}
                  <DynamicRepeaterItemOverlay
                    show={dragItemId !== null}
                    title={t("page.surveys.create-or-edit.question.label", { count: dragItemId! + 1 })}
                    headerActions={
                      fields.length > 1 &&
                      (defaultFormValues.startDate ? defaultFormValues.startDate > clockDate : true) ? (
                        <IconButton
                          size="sm"
                          variant="filled"
                          tone="error"
                          title={t("page.surveys.create-or-edit.question.delete")}
                          onClick={() => null}
                          icon={<Trash02Icon />}
                          disabled
                        />
                      ) : undefined
                    }
                  >
                    <FormQuestion<CreateOrEditSurveyFormValues>
                      idx={dragItemId!}
                      control={form.control}
                      updateField={() => null}
                      types={questionTypes}
                      question={fields[dragItemId!]}
                      showOptions={fields[dragItemId!]?.type === "multipleChoice"}
                      showUseForAnalysisOption={category === "feelAtHome"}
                      isUseForAnalysisDisabled={isUseForAnalysisSelected && !fields[dragItemId!]?.useForAnalysis}
                    />
                  </DynamicRepeaterItemOverlay>
                </DynamicRepeater>
              </Form.Body>
              <Form.Footer
                renderPrimaryAction={() => (
                  <Button
                    data-testid="add-question-btn"
                    variant="ghostPrimary"
                    onClick={() => append({ type: "open", question: "", options: [] }, { shouldFocus: false })}
                    icon={<PlusIcon />}
                  >
                    {t("page.surveys.create-or-edit.question.add")}
                  </Button>
                )}
              />
            </WizardStep>
          </Wizard>
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
